import { useDispatch } from "react-redux";
import useQueryWithErrorHandling from "utils/useQuery";
import {
  midgardClients,
  DATA_DUMP_AGGREGATE_QUERY,
} from "providers/MidgardProvider";
import {
  useProcessIdOnSingleProcessPage,
  useLotInfoForProcessId,
} from "./LotInfoBar/lotInfoUtils";
import { clearLotCount, updateLotCount } from "providers/GlobalStateProvider";
import { DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_INTERVAL } from "constants/intervals";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useUnmount } from "utils/customHooks/genericHooks";
import { DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { useIsTimeRangeSmallEnough } from "segments/DataDumpGetter/DataDumpGetter";
import { useIsLotInfoPaused } from "store/atoms";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useRef } from "react";
import { useInterval } from "react-use";

export default function LotCountGetter() {
  const dispatch = useDispatch();
  const [isLotInfoPaused] = useIsLotInfoPaused();
  const processId = useProcessIdOnSingleProcessPage();
  const lotInfoForProcess = useLotInfoForProcessId(processId);
  const lotStartDateMs = lotInfoForProcess?.lotStartDateMs;
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);

  useUnmount(() => {
    if (processId) {
      dispatch(clearLotCount({ processId }));
    }
  });

  const variables = {
    ge: String(lotStartDateMs ?? 0),
    lt: String(now.current),
    machemetryIds: [processId],
  };

  const { isTimeRangeSmallEnough } = useIsTimeRangeSmallEnough(
    lotStartDateMs ? now.current - lotStartDateMs : Infinity
  );

  const skip =
    isLotInfoPaused || !lotStartDateMs || !processId || !isTimeRangeSmallEnough;

  // UPDATE LOT COUNT
  // get netOutput / count since lot started, to forecast lot completion date

  return skip ? null : <Query {...{ variables }} />;
}

function Query({ variables }: any) {
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);

  const processId = useProcessIdOnSingleProcessPage();

  const dispatch = useDispatch();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();

  useQueryWithErrorHandling(
    DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_TITLE,
    DATA_DUMP_AGGREGATE_QUERY,
    {
      client,
      variables,
      onError: err => {
        console.log(
          `LotCountGetter data dump gone wrong: ${JSON.stringify(variables)}`
        );
        // useQueryWithErrorHandling will log the error + error code via handleError
      },
      onCompleted: data => {
        if (!processId) {
          return;
        }
        const lotCount = data?.dataDumpAggregate?.netOutput;
        if (lotCount || lotCount === 0) {
          dispatch(updateLotCount({ processId, lotCount }));
        }
      },
      pollInterval:
        DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_INTERVAL *
        multiplyNetworkRequestIntervals,
    }
  );
  return null;
}
